import logoUrl from '@/assets/digital-codex-logo.svg';
import markWhiteUrl from '@/assets/digital-codex-mark-white.svg';
import { cn } from '@/shared/lib/utils';

/**
 * The Digital Codex logo.
 *
 * Always renders the supplied asset — never a CSS or text recreation (§84).
 * The asset is the full lockup (mark + wordmark, viewBox 178×117) whose
 * gradient stops are the source of the entire brand palette.
 *
 * `variant="mark-white"` is the monochrome mark, for dark or saturated
 * backgrounds where the gradient lockup would not read.
 *
 * The aspect ratio is fixed by `width`/`height` so the logo can never be
 * stretched or cropped by a flex parent.
 */
interface LogoProps {
  variant?: 'full' | 'mark-white';
  className?: string;
  /** Rendered height in pixels; width follows the intrinsic aspect ratio. */
  height?: number;
}

const ASPECT = {
  full: 178.167 / 116.529,
  'mark-white': 66.476 / 43.587,
} as const;

export function Logo({ variant = 'full', className, height = 32 }: LogoProps) {
  const src = variant === 'full' ? logoUrl : markWhiteUrl;

  return (
    <img
      src={src}
      alt="Digital Codex"
      height={height}
      width={Math.round(height * ASPECT[variant])}
      className={cn('block select-none', className)}
      // Decorative in context — the accessible name comes from surrounding
      // text where one exists. Kept as alt text for the standalone case.
      draggable={false}
    />
  );
}

/**
 * Logo plus the "Admin" qualifier, for the sidebar and login screen.
 * Separated from `Logo` so the bare asset stays reusable.
 */
export function LogoLockup({ className, height = 30 }: { className?: string; height?: number }) {
  return (
    <div className={cn('flex items-center gap-2.5', className)}>
      <Logo height={height} />
      <span className="border-border text-muted-foreground border-s ps-2.5 text-[11px] font-semibold tracking-[0.14em] uppercase">
        Admin
      </span>
    </div>
  );
}
